import splitArrayKey from '@bybeam/platform-lib/answers/splitArrayKey';
import { checkFeatures } from '@bybeam/platform-lib/features/check';
import {
  ApplicantProfile,
  ApplicantProfileConfig,
  ApplicantProfileConfiguration,
  ApplicationAnswers,
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationQuestionGroup,
  ApplicationQuestionKeys,
  ApplicationSection,
  FeatureName,
  Field,
  IntroPage,
  Program,
  TaxForm,
  TaxFormType,
} from '@bybeam/platform-types';
import { datadogLogs } from '@datadog/browser-logs';
import { checkIfTaxFormWasSubmitted } from '@platform-ui-common/hooks/useTaxForms.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import { flatten } from 'flat';
import { makeRoute } from '../Routes.js';
import { getApplicantProfileConfig } from '../applicantProfile.js';
import { SpecialSectionKeys } from './configs/standards/types.js';
import {
  getQuestionFields,
  getQuestionRoute,
  isQuestionEnabled,
  isQuestionGroupEnabled,
} from './question.js';
import { getSectionRoute } from './section.js';

export interface ApplicationConfigDefinitions {
  definitions: {
    introPages?: { [key: string]: IntroPage };
    sections?: { [key: string]: ApplicationSection };
    questions?: { [key: string]: ApplicationQuestion };
  };
}

export function getApplicantSections(config: ApplicationConfig): ApplicationSection[] {
  return config.sections.filter(({ key }) => key !== SpecialSectionKeys.AdminOnly);
}

export function getField(config: ApplicationConfig, key: string): Field {
  return config.sections
    .flatMap(({ questionGroups }) =>
      questionGroups.flatMap(({ questions }) => questions.flatMap(({ fields }) => fields)),
    )
    .find(({ key: fieldKey }) => fieldKey === key);
}

// Returns the question with the given key, if it exists in the config.
// Only checks the questions in config.sections, does not look in config.portalSections
export function getQuestion(config: ApplicationConfig, key: string): ApplicationQuestion {
  return config.sections
    .flatMap(({ questionGroups }) => questionGroups.flatMap(({ questions }) => questions))
    .find(({ key: qKey }) => qKey === key);
}

export function getQuestionGroupByQuestion(
  config: ApplicationConfig,
  questionKey: string,
): ApplicationQuestionGroup {
  return config.sections
    .flatMap(({ questionGroups }) => questionGroups)
    .find(({ questions }) => questions.find(({ key: qKey }) => qKey === questionKey));
}

// Gets the route to the screen that should be shown after the given question, according
// to the given config. Uses the given answers to check skip logic. If no next question
// can be found, returns undefined.
export function getNextPage(
  config: ApplicationConfig,
  question: ApplicationQuestion,
  answers: Partial<ApplicationAnswers>,
  program: Program,
  submittedTaxForms: TaxForm[],
): string {
  const section = getApplicantSections(config).find(({ questionGroups }) =>
    questionGroups.some(({ questions }) => questions.includes(question)),
  );
  if (!section) {
    datadogLogs.logger.error(
      `Application Config: Could not find section for question ${question.key}`,
    );
    return undefined;
  }

  const w9TaxFormWasAlreadySubmitted = checkIfTaxFormWasSubmitted(
    submittedTaxForms,
    TaxFormType.W9,
  );
  const questions = config.sections.flatMap(({ questionGroups }) =>
    questionGroups.flatMap(({ questions }) => questions),
  );
  // We grab the second last question from the last section (since the W9 tax form always comes before the last question).
  // Then we make sure that (if we have the necessary features and the tax form has not been submitted yet)
  // the next question is the W9 tax form.
  const secondLastQuestionIndex = questions.length - 2;
  const { key: secondLastQuestionKey } = questions[secondLastQuestionIndex];

  const hasNecessaryFeatures = checkFeatures(program?.features, [
    FeatureName.TaxFormsW9Collection,
    FeatureName.PaymentsPartnerIssued,
  ]);
  if (
    hasNecessaryFeatures &&
    !w9TaxFormWasAlreadySubmitted &&
    question.key === secondLastQuestionKey
  ) {
    return ApplicantRoutes.W9_TAX_FORM;
  }

  const allTheQuestions = section.questionGroups.flatMap(({ questions }) => questions);

  const remainingQuestions = allTheQuestions.slice(allTheQuestions.indexOf(question) + 1);

  const nextEnabledQuestion = remainingQuestions.find((q) => {
    const questionGroup = getQuestionGroupByQuestion(config, q.key);
    return isQuestionGroupEnabled(questionGroup, answers) && isQuestionEnabled(q, answers);
  });

  if (nextEnabledQuestion) {
    const currentQuestionQuestionGroup = getQuestionGroupByQuestion(config, question.key);

    const nextEnabledQuestionQuestionGroup = getQuestionGroupByQuestion(
      config,
      nextEnabledQuestion.key,
    );

    if (
      currentQuestionQuestionGroup !== nextEnabledQuestionQuestionGroup &&
      nextEnabledQuestionQuestionGroup?.overview
    ) {
      return makeRoute(ApplicantRoutes.QUESTION_GROUP_OVERVIEW, {
        groupKey: nextEnabledQuestionQuestionGroup.key,
      });
    }

    return getQuestionRoute(nextEnabledQuestion);
  }

  const nextSection = config.sections[config.sections.indexOf(section) + 1];
  return nextSection ? getSectionRoute(nextSection, answers) : undefined;
}

const getSectionFieldKeys = (section: ApplicationSection): string[] =>
  section.questionGroups.flatMap(({ questions }) =>
    questions.flatMap(({ fields }) => getQuestionFields(fields).map(({ key }) => key)),
  );

const keyInList = (list: string[], key: string): boolean =>
  list.some((configKey) => key?.toLowerCase().includes(configKey.toLowerCase()));

const addressKeyRegex = /addressid/gi;

export const sectionHasPrefilledAnswers = (
  section: ApplicationSection,
  applicantProfile: ApplicantProfile,
  partnerConfig: ApplicantProfileConfig,
): boolean => {
  const sectionKeys = getSectionFieldKeys(section);
  const partnerConfigMatch =
    partnerConfig?.profileKeys?.some(({ key }) => keyInList(sectionKeys, key)) ||
    keyInList(sectionKeys, ApplicationQuestionKeys.MailingAddress);

  const applicantProfileMatch = Object.keys(
    // biome-ignore lint/suspicious/noExplicitAny: only considering the keys
    flatten<ApplicationAnswers, Record<string, any>[]>({
      ...(applicantProfile?.answers ?? {}),
      ...(applicantProfile?.mailingAddress
        ? { [ApplicationQuestionKeys.MailingAddress]: applicantProfile.mailingAddress.id }
        : {}),
    }),
  ).some((key) => {
    const { key: cleanedKey } = splitArrayKey(key);
    if (cleanedKey.match(addressKeyRegex))
      return keyInList(
        sectionKeys.map((sectionKey) => `${sectionKey}Id`),
        cleanedKey,
      );
    return keyInList(sectionKeys, cleanedKey);
  });
  return partnerConfigMatch && applicantProfileMatch;
};

export const applicationHasPrefilledAnswers = (
  applicationConfig: ApplicationConfig,
  applicantProfile: ApplicantProfile,
  applicantProfileConfigs: ApplicantProfileConfiguration[],
): boolean => {
  const partnerConfig = getApplicantProfileConfig(applicantProfileConfigs, applicantProfile);
  return !!applicationConfig?.sections?.find((section) =>
    sectionHasPrefilledAnswers(section, applicantProfile, partnerConfig),
  );
};
